import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import qz from 'qz-tray';

import OrderActions from '~redux/Order/actions';

import OrderDetails from '../../screens/Home/components/Orders/components/OrdersTable/components/OrderDetails';

import { usePrinter, usePrinterSettings, Print, LayoutPrint } from './';

export const onPrint = (order, printer, onAfterPrint) => (target) => {
  if (!qz.websocket.isActive()) {
    return Promise.resolve();
  }

  const doc = new DOMParser().parseFromString(
    target.contentWindow.document.documentElement.outerHTML,
    'text/html'
  );
  const config = qz.configs.create(printer);
  const printConfig = { type: 'pixel', format: 'html', flavor: 'plain' };
  return qz
    .print(config, [{ ...printConfig, data: doc.documentElement.outerHTML }])
    .then(onAfterPrint)
    .catch((e) => {
      console.error('Failed to print order', order, e);
      onAfterPrint();
    });
};

export function PrinterManager() {
  const dispatch = useDispatch();
  const [isPrinting, setIsPrinting] = useState(false);

  const [printerSettings] = usePrinterSettings();
  const { connect, connected, printers } = usePrinter();

  useEffect(() => {
    const disconnect = connect();
    return disconnect;
  }, [connect]);

  const orders = useSelector((state) => state.order?.print);

  const connectedPrinter = printers.find((printer) => printer === printerSettings.printer);
  const shouldPrint = connected && printerSettings.enableAutoPrinting && connectedPrinter;
  const [orderToPrint] = shouldPrint ? [...orders] : [];

  const onAfterPrint = useCallback(
    (order) => () => {
      setIsPrinting(false);
      dispatch(OrderActions.removeOrderPrintQueue(order.id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (orderToPrint && !isPrinting) {
      setIsPrinting(true);
    }
  }, [orderToPrint, isPrinting]);

  const reactToPrintProps = useMemo(
    () =>
      orderToPrint
        ? {
            print: onPrint(orderToPrint, printerSettings.printer, onAfterPrint(orderToPrint)),
            removeAfterPrint: true
          }
        : null,
    [orderToPrint, printerSettings.printer, onAfterPrint]
  );

  if (!orderToPrint || !isPrinting || !reactToPrintProps) {
    return null;
  }

  return (
    <Print
      key={orderToPrint.id}
      componentToPrint={
        <LayoutPrint>
          <OrderDetails item={orderToPrint} />
        </LayoutPrint>
      }
      reactToPrintProps={reactToPrintProps}
    />
  );
}
